import { Button, InputLabel, Stack } from "@mui/material";
import { API } from "aws-amplify";
import { useCloseAndReset } from "hooks/useCloseAndReset";
import { useCreateApiCall } from "hooks/useCreateApiCall";
import {
  required
} from "models/validations";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { GlobalContext } from "store/globalContext";
import { useAppDispatch } from "store/hooks";
import { showError } from "store/reducers/errorReducer";
import { DialogWindow } from "UI/DialogWindow";
import { FormTextField } from "UI/FormTextField";

/** @type {import("types/commonTypes").NewProviderData}*/
const defaultFormData = {
  name: "",
  speciality: "",
};

export const AddNewProviderDialog = ({
  open,
  handleClose,
  refreshList,
  organizationId,
  setResultValue,
}) => {
  const { createApiCall } = useCreateApiCall();
  const dispatch = useAppDispatch();
  const { showWarning } = useContext(GlobalContext);
  const [isCreating, setIsCreating] = useState(false);

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: defaultFormData,
  });

  const forceCloseAndReset = () => {
    reset(defaultFormData);
    handleClose();
  };

  const onSubmit = createApiCall(async () => {
    setIsCreating(true);
    const provider = getValues();
    const body = {
      provider: {
        ...provider,
        organizationId,
      },
    };

    body.provider.name = body.provider.name.trim();
    body.provider.speciality = body.provider.speciality.trim();

    const result = await API.put("restApi", "/providers", {
      body,
    });

    if (result?.error) {
      console.error(result.error);
      dispatch(showError(result.error));
      setIsCreating(false);
      return;
    }

    showWarning({
      title: `Provider added successfully`,
      asNotification: true,
    });

    if (result?.id) {
      setResultValue(result);
    }

    setIsCreating(false);
    refreshList();
    forceCloseAndReset();
  }, (error) => {
    console.error(error);
    dispatch(showError(error?.error ?? error?.message ?? "Something went wrong"));
    setIsCreating(false);
  });

  const closeAndReset = useCloseAndReset(forceCloseAndReset, isDirty);

  return (
    <DialogWindow
      open={open}
      withCloseButton
      handleClose={closeAndReset}
      title={"Add New Provider"}
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack mt={2} gap={2} width={"90vw"} maxWidth={"480px"}>
            <Stack direction={"column"} gap={2}>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="name">Name</InputLabel>
                <FormTextField
                  id="name"
                  name="name"
                  control={control}
                  rules={{ required }}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
              <Stack alignItems={"flex-start"}>
                <InputLabel htmlFor="speciality">Speciality (optional)</InputLabel>
                <FormTextField
                  id="speciality"
                  name="speciality"
                  control={control}
                  InputProps={{
                    autoComplete: "off",
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
        </form>
      }
      actions={
        <Stack
          direction={"column"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"100%"}
          gap={2}
          mt={6}
          mb={2}
        >
          <Button
            disabled={isCreating}
            variant="contained"
            onClick={handleSubmit(onSubmit)}
          >
            Add New Provider
          </Button>
        </Stack>
      }
    />
  );
};
